<template>
	<VDropdown
		:shown="isOpen"
		placement="right-start"
		distance="8"
		:skidding="-8"
		:disabled="!isDropdownEnabled"
		@auto-hide="$emit('toggle-open')"
		@apply-hide="$emit('update:is-open', false)"
	>
		<div class="user-assign-select__avatar">
			<SuiAvatar
				v-if="dynamicOwnerInitials"
				v-tooltip="{
					content: dynamicOwnerLangVar,
					popperClass: `user-assign-select-owner-tooltip`,
				}"
				as="button"
				variant="dashed"
				color="#717a89"
				:initials="dynamicOwnerInitials"
				:disabled="true"
				size="x-small"
			/>
			<SuiAvatar
				v-else
				v-tooltip="{
					content: ownerTooltip,
					popperClass: `user-assign-select-owner-tooltip`,
				}"
				as="button"
				type="button"
				:disabled="!isDropdownEnabled"
				:url="ownerProfileImage"
				:aria-label="owner?.name"
				:size="avatarSize"
				show-border
				:initials="owner?.initials || ''"
				:color="owner?.hslColor || ''"
				@click="$emit('avatar-click')"
				@dblclick="$emit('avatar-dblclick')"
				@keypress="$emit('avatar-click')"
			/>
			<div
				v-if="size === UserAssignSelectSize.LARGE"
				class="user-assign-select__avatar-name"
			>
				{{ owner?.firstName }}
			</div>
		</div>
		<template #popper>
			<SuiSelectList>
				<template #header>
					<SuiSelectListHeaderWithSearch
						:search-query="searchQuery"
						:search-placeholder="$t('user_assign_select_find_user')"
						@close="$emit('toggle-open')"
						@search="$emit('update:search-query', $event)"
					>
						<template #title>
							{{ ownerTitle }}
						</template>
						<template #close>
							{{ $t('close') }}
						</template>
					</SuiSelectListHeaderWithSearch>
				</template>
				<div
					v-if="potentialOwners && potentialOwners?.length > 0"
					:class="{
						'user-assign-select__dropdown--disabled':
							!isModifyEnabled,
					}"
				>
					<SuiSelectListButton
						v-for="user in potentialOwners"
						:key="user.id"
						:aria-selected="entityOwner?.id === user.id"
						:aria-label="
							$t('user_assign_select_user_label', {
								name: user.name,
							})
						"
						:active="entityOwner?.id === user.id"
						class="user-assign-select__user-button"
						@click="$emit('owner-click', user)"
					>
						<SuiAvatar
							size="x-small"
							show-border
							:url="user.profilePicture?.mini"
							:class="{
								'user-assign-select__cupo-avatar':
									user.type === UserType.Cupo,
							}"
							:initials="user.initials ?? ''"
							:color="user.hslColor ?? ''"
						/>
						<p
							:class="{
								'user-assign-select__user-name--inactive':
									user.status === UserStatus.Inactive,
							}"
						>
							{{ user.name }}
						</p>
						<SuiIcon
							v-if="entityOwner?.id === user.id"
							name="icon-tick-16"
							class="user-assign-select__tick"
						/>
					</SuiSelectListButton>
					<UserAssignSelectListSkeleton
						v-if="loadingUsersAndGroups"
					/>
				</div>
				<SuiSelectListEmpty v-else>
					{{ $t('search.noResults') }}
				</SuiSelectListEmpty>
				<template v-if="isModifyEnabled" #footer>
					<SuiButton variant="primary" @click="$emit('assign-owner')">
						{{ $t('apply') }}
					</SuiButton>
				</template>
			</SuiSelectList>
		</template>
	</VDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
	SuiSelectList,
	SuiSelectListHeaderWithSearch,
	SuiSelectListButton,
	SuiSelectListEmpty,
	SuiAvatar,
	SuiIcon,
	SuiButton,
} from '@developers/scoro-ui';
import {
	type UserAssignSelectUserFragment,
	UserType,
	UserStatus,
	DynamicOwnerEnum,
} from '@/generated/graphql';
import { UserAssignSelectSize } from '../../constants';
import { type ValueOf } from '@/types';
import UserAssignSelectListSkeleton from './List/UserAssignSelectListSkeleton.vue';
import { type UserAssignAvatarSize } from '../../types';
import { useTranslation } from '@/composables/useTranslation';
import { getInitials, getUserTooltip, getUserProfileImage } from '../../utils';

export interface UserAssignSelectOwnerDropdownProps {
	isOpen: boolean;
	isDropdownEnabled: boolean;
	isModifyEnabled: boolean;
	owner: UserAssignSelectUserFragment | null | undefined;
	dynamicOwner?: DynamicOwnerEnum | null;
	entityOwner: UserAssignSelectUserFragment | null | undefined;
	potentialOwners: UserAssignSelectUserFragment[];
	ownerTitle?: string;
	searchQuery: string;
	avatarSize: UserAssignAvatarSize;
	loadingUsersAndGroups: boolean;
	size: ValueOf<typeof UserAssignSelectSize>;
}

const { t } = useTranslation();

const { dynamicOwner, owner, size } =
	defineProps<UserAssignSelectOwnerDropdownProps>();

const dynamicOwnerLangVar = computed(() => {
	if (!dynamicOwner) {
		return null;
	}

	return dynamicOwner === DynamicOwnerEnum.TaskCreator
		? t('task_creator')
		: t('project_manager');
});

const dynamicOwnerInitials = computed(() => {
	if (!dynamicOwnerLangVar.value) {
		return null;
	}

	return getInitials(dynamicOwnerLangVar.value);
});

const ownerTooltip = computed(() => getUserTooltip(owner));

const ownerProfileImage = computed(() => getUserProfileImage(owner, size));

defineEmits<{
	'update:is-open': [value: boolean];
	'update:search-query': [value: string];
	'toggle-open': [];
	'avatar-click': [];
	'avatar-dblclick': [];
	'owner-click': [user: UserAssignSelectUserFragment];
	'assign-owner': [];
}>();
</script>
